import React, { useState, useCallback } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { useTenant, useTenantSetting } from "@nestoca/multi-tenant";
import {
  Button,
  Flex,
  useBreakpointValue,
  Menu,
  ItemElementProps,
} from "@nestoca/ui";
import { useAnalytics } from "@shared/analytics";
import { useGetAccount, useMutateAccount } from "@shared/api/hooks/account";
import { UserSettingsModal } from "@shared/ui/components/modals";
import { capitalizeFirstLetters } from "@shared/utils";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { BsBuilding } from "react-icons/bs";
import { BsGear } from "react-icons/bs";
import { IoIosLogOut } from "react-icons/io";
import { IoLanguage } from "react-icons/io5";
import { MdOutlinePrivacyTip } from "react-icons/md";
import { toast } from "react-toastify";

import { NavbarLogo } from "./navbar-logo";
import styles from "./navbar.module.scss";

const iconStyle = {
  minWidth: 16,
  marginRight: 10,
  minHeight: 16,
};

export const AuthenticatedHeader = ({ className }: { className?: string }) => {
  const { track } = useAnalytics();
  const tenant = useTenant();
  const {
    settings: { showPrivacyPolicyMenu = true, showMyPortfolioButton = true },
  } = useTenantSetting("cmaFormSettings");
  const router = useRouter();
  const { locale, asPath, defaultLocale, replace } = router;
  const portfolioHref = locale === defaultLocale ? "/" : `/${locale}`;
  const { logout } = useAuth0();
  const { t } = useTranslation();
  const { data: account, isPending, isError, error } = useGetAccount();
  const accountMutation = useMutateAccount();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const isMobile = useBreakpointValue({ default: true, md: false });

  const onLanguageClick = useCallback(() => {
    const selectedLanguage = locale === "en" ? "fr" : "en";

    if (!account) {
      return toast(t("failedToSave", { ns: "error" }), { type: "error" });
    }

    accountMutation.mutateAsync(
      { ...account, preferredLanguage: selectedLanguage },
      {
        onSuccess: () => {
          replace(asPath, asPath, {
            locale: selectedLanguage,
          });
        },
        onError: () => {
          toast(t("dataUpdatedFailure", { ns: "applications" }), {
            type: "error",
          });
        },
      }
    );
  }, [account, asPath, replace, t, accountMutation, locale]);

  if (isPending || !tenant || isMobile) {
    return null;
  }

  // When query is failing, we want to throw the error to the error boundary
  // it happen only when the hook is in the layout function on th `_app` `getLayout`
  if (isError) {
    throw error;
  }

  const handleLogout = () => {
    track({ event: "logout" });
    logout({
      logoutParams: {
        /**
         * TODO: This is temporary for a hotfix to prod. We need to figure out a better way to customize this.
         * Maybe an env variable?
         */
        returnTo: `${window.location.origin}/`,
        federated: true,
      },
    });
  };

  const applicantInitials = !isError
    ? capitalizeFirstLetters(t, account?.firstName as string, account?.lastName)
    : "";

  const MenuButton = () => {
    return (
      <Button
        as={Flex}
        variant="alternative"
        // @ts-expect-error - Button component accepts Flex props but TypeScript doesn't recognize it
        // because of polymorphic component
        gap={3}
        key="create-button"
        size="large"
        align="center"
        justify="center"
        className={styles.applicant_menu}
        data-dd-action-name="avatar menu"
      >
        {applicantInitials}
      </Button>
    );
  };

  const applicantMenuItems: ItemElementProps[] = [
    {
      icon: (
        <BsGear
          key="icon-gear"
          size={16}
          color="var(--color-brand)"
          style={iconStyle}
        />
      ),
      id: "userSettings",
      key: "userSettings",
      title: t("userSettings"),
      onClick: () => {
        setIsModalVisible(true);
        track({
          event: "view_settings",
        });
      },
      e2ePrefix: "userSettings",
    },
    {
      icon: (
        <IoLanguage
          key="icon-language"
          size={16}
          color="var(--color-brand)"
          style={iconStyle}
        />
      ),
      id: "language",
      key: "language",
      title: t("language.update", { locale: locale === "en" ? "FR" : "EN" }),
      onClick: onLanguageClick,
      e2ePrefix: "language",
    },
    ...(showPrivacyPolicyMenu
      ? [
          {
            icon: (
              <MdOutlinePrivacyTip
                key="icon-privacy"
                size={16}
                color="var(--color-brand)"
                style={iconStyle}
              />
            ),
            id: "privacyPolicy",
            key: "privacyPolicy",
            title: t("privacyPolicy"),
            onClick: () => {
              if (locale === "fr") {
                window.open(
                  tenant.privacyPolicyFRURL || tenant.privacyPolicyURL,
                  "_blank"
                );
              } else {
                window.open(tenant.privacyPolicyURL, "_blank");
              }
            },
            e2ePrefix: "privacyPolicy",
          },
        ]
      : []),
    {
      icon: (
        <IoIosLogOut size={16} color="var(--color-brand)" style={iconStyle} />
      ),
      id: "logout",
      key: "logout",
      title: t("logout"),
      onClick: handleLogout,
      e2ePrefix: "logout",
    },
  ];

  return (
    <Flex
      className={clsx(styles.header, className)}
      direction="row"
      align="center"
      justify="between"
    >
      <UserSettingsModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        handleLogout={handleLogout}
      />
      <Flex align="center" gap={5}>
        <NavbarLogo />
        {!isMobile && showMyPortfolioButton && (
          <Button
            as="a"
            // Polymorphic component, attribute will be passed to the underlying `as` element
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            href={portfolioHref}
            title={`${t("portfolio")}`}
            variant="primary"
            size="small"
            e2ePrefix="my-portfolio"
            data-dd-action-name="my portfolio"
          >
            <BsBuilding size={15} />
            {t("myPortfolio")}
          </Button>
        )}
      </Flex>
      <Menu
        menuItems={[
          {
            sectionAtoms: applicantMenuItems,
          },
        ]}
        selectionMode="none"
        buttonChildren={MenuButton}
        menuClassName={styles.applicant_menu_dropdown}
        withIcons
      />
    </Flex>
  );
};
